// "Sign in with Yarn" on the client. `<SphinxAuthGate>` wraps the app
// (main.tsx): it asks `/auth/me` once, renders the sign-in screen when there is
// no session, and flips back to it whenever an API wrapper reports a 401
// `unauthenticated` (`requestSignIn` in lib/sphinx-auth.ts). The session
// itself is an httpOnly cookie; `<SphinxSessionBadge>` (sidebar footer) shows
// who is signed in with a Sign out.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Button } from "@sphinx/design-system/button";
import {
  fetchSphinxMe,
  gateDecisionFor,
  goToSignIn,
  onSignInRequired,
  signOut,
  type SphinxMe,
} from "@/lib/sphinx-auth";
import { setSphinxAccessKey } from "@/lib/sphinx-access-key";

const MeContext = createContext<SphinxMe | null>(null);

/** The current sphinx identity (null while unknown / auth disabled / key). */
export function useSphinxMe(): SphinxMe | null {
  return useContext(MeContext);
}

type GateState =
  | { kind: "checking" }
  | { kind: "signed-in"; me: SphinxMe }
  | { kind: "sign-in"; reason?: string }
  /** A stored access key, no Yarn session: half the app would 401. */
  | { kind: "stored-key" };

export function SphinxAuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>({ kind: "checking" });

  useEffect(() => {
    let cancelled = false;
    fetchSphinxMe()
      .then((me) => {
        if (cancelled) return;
        const decision = gateDecisionFor(me);
        setState(
          decision === "app" && me
            ? { kind: "signed-in", me }
            : decision === "stored-key"
              ? { kind: "stored-key" }
              : { kind: "sign-in" }
        );
      })
      .catch((err: unknown) => {
        // The server is unreachable or broken: let the app render and show
        // its own errors rather than a sign-in page that cannot help.
        console.warn("[sphinx auth] /auth/me failed", err);
        if (!cancelled) setState({ kind: "signed-in", me: { mode: "disabled", user: null } });
      });
    const off = onSignInRequired(() =>
      setState({ kind: "sign-in", reason: "Your session has expired." })
    );
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  if (state.kind === "checking") return null;
  if (state.kind === "stored-key") return <SphinxStoredKeyScreen />;
  if (state.kind === "sign-in") return <SphinxSignInScreen reason={state.reason} />;
  return <MeContext.Provider value={state.me}>{children}</MeContext.Provider>;
}

export function SphinxSignInScreen({ reason }: { reason?: string }) {
  return (
    <div
      data-testid="sphinx-sign-in"
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-8 text-foreground"
    >
      <img src="/sphinx.svg" alt="Sphinx" className="h-16 w-16" />
      <h1 className="text-xl font-semibold">Sphinx</h1>
      <p className="max-w-sm text-center text-sm text-muted-foreground">
        {reason ?? "Yarn's MCP eval harness."} Sign in with your Yarn account to
        continue.
      </p>
      <Button onClick={goToSignIn}>Sign in with Yarn</Button>
    </div>
  );
}

/**
 * A browser holding a service access key but no Yarn session: the sphinx API
 * accepts it, so the app would render, but nothing that talks to an MCP server
 * works and there is no identity to sign out of. Offer the way out.
 */
export function SphinxStoredKeyScreen() {
  return (
    <div
      data-testid="sphinx-stored-key"
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-8 text-foreground"
    >
      <img src="/sphinx.svg" alt="Sphinx" className="h-16 w-16" />
      <h1 className="text-xl font-semibold">Sphinx</h1>
      <p className="max-w-sm text-center text-sm text-muted-foreground">
        This browser is using a service access key, which has no identity and
        cannot reach the MCP connectors. Sign in with your Yarn account instead.
      </p>
      <Button
        onClick={() => {
          setSphinxAccessKey(null);
          goToSignIn();
        }}
      >
        Sign in with Yarn
      </Button>
    </div>
  );
}

/** Email + Sign out, only when a Yarn session is active. */
export function SphinxSessionBadge() {
  const me = useSphinxMe();
  if (!me || me.mode !== "session" || !me.user) return null;
  const label = me.user.email;
  return (
    <div
      data-testid="sphinx-session-badge"
      className="flex items-center justify-between gap-2 px-2 py-1 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden"
    >
      <span className="truncate" title={label}>
        {label}
      </span>
      <button
        type="button"
        onClick={() => void signOut()}
        className="shrink-0 underline-offset-2 hover:underline"
      >
        Sign out
      </button>
    </div>
  );
}
